import * as _ from 'lodash';

import type { Match, Revision, RevisionLicense } from '../../types';

export type LicenseGroup = {
  licenses: Array<Partial<RevisionLicense>>;
  editing: boolean;
};

export type LicenseGroups = LicenseGroup[];

export const createLicenseGroup = (licenses: RevisionLicense[], editing: boolean = false): LicenseGroup => ({
  licenses,
  editing,
});

/**
 * Returns the license groups found in the licenses array where grouping is done according to LicenseGroup.
 * Licenses that are not in a LicenseGroup are their own group.
 */
export const groupRevisionLicense = (licenses: RevisionLicense[]): RevisionLicense[][] =>
  Object.values(
    // Use the licenseGroupId for grouping for licenses where licenseGroupId is defined, but otherwise use a string that
    // contains the RevisionLicense ID so that we don't create invalid groups when a licenseGroupId matches the value of
    // a RevisionLicense's ID.
    _.groupBy(licenses, ({ id, licenseGroupId }: RevisionLicense) => licenseGroupId || `_${id}`),
  );

/**
 * Returns the match groups found in the matches array where grouping is done according to the RevisionLicense's
 * LicenseGroup of each match. Matches that do not fall under a LicenseGroup are their own group.
 */
export const groupMatches = (matches: Match[]): Match[][] =>
  Object.values(
    // Use the licenseGroupId for grouping for matches where licenseGroupId is defined, but otherwise use a string that
    // contains the Match ID so that we don't create invalid groups.
    _.groupBy(matches, ({ id, RevisionLicense: { licenseGroupId } }: Match) => licenseGroupId || `_${id}`),
  );

export const getLicenseGroupsFromRevision = (revision: Revision): LicenseGroups =>
  groupRevisionLicense(revision.licenses).map(
    (licenses) => createLicenseGroup(licenses), // can't just pass the fn, 2nd arg doesn't pick the default value
  );

/**
 * Update a group by applying a given function to its current value.
 */
const updateGroup = (licenseGroups: LicenseGroups, groupIndex, fn: (group: LicenseGroup) => LicenseGroup) =>
  licenseGroups.map((group, gIndex) => gIndex !== groupIndex ? group : fn(group));

/**
 * Update a license in a group by applying a given function to its current value.
 */
const updateLicense = (
  licenseGroups: LicenseGroups,
  groupIndex: number,
  licenseIndex: number,
  fn: (license: Partial<RevisionLicense>) => Partial<RevisionLicense>,
): LicenseGroups =>
  updateGroup(licenseGroups, groupIndex, group => ({
    ...group,
    licenses: group.licenses.map((license, lIndex) => lIndex !== licenseIndex ? license : fn(license)),
  }));

/**
 * Show or hide license form.
 */
export const toggleEditGroup = (licenseGroups: LicenseGroups, groupIndex: number): LicenseGroups =>
  updateGroup(licenseGroups, groupIndex, group => ({ ...group, editing: !group.editing }));

/**
 * Add a license to a group, add a license to a new group or change a license from a group.
 */
export const addLicense = (
  licenseGroups: LicenseGroups,
  groupIndex: number,
  licenseIndex: number | null,
  customLicense: RevisionLicense,
): LicenseGroups => {
  if (licenseIndex !== null) {
    // Change license from a group
    return updateLicense(licenseGroups, groupIndex, licenseIndex, (license) => ({
      ...customLicense,
      id: license.id,
      title: customLicense.title || customLicense.licenseId,
      licenseGroupId: license.licenseGroupId,
    }));
  }

  if (groupIndex >= licenseGroups.length) {
    // add license in a new group
    return [...licenseGroups, createLicenseGroup([customLicense])];
  }

  // append to group
  return updateGroup(licenseGroups, groupIndex, group => ({
    ...group,
    licenses: [...group.licenses, customLicense],
  }));
};

/**
 * Remove a license from a group. Remove the group as well if it ends up empty.
 */
export const deleteLicense = (
  licenseGroups: LicenseGroups,
  groupIndex: number,
  licenseIndex: number,
): LicenseGroups => {
  const newLicenses = licenseGroups[groupIndex].licenses.filter((_lic, i) => i !== licenseIndex);
  return newLicenses.length === 0
    ? licenseGroups.filter((_g, i) => i !== groupIndex)
    : updateGroup(licenseGroups, groupIndex, group => ({ ...group, licenses: newLicenses }));
};

/**
 * Take out a license from a group and add a new group with it.
 */
export const splitLicense = (
  licenseGroups: LicenseGroups,
  groupIndex: number,
  licenseIndex: number,
  license: RevisionLicense,
): LicenseGroups =>
  addLicense(
    deleteLicense(licenseGroups, groupIndex, licenseIndex),
    licenseGroups.length,
    null,
    license,
  );

/**
 * Add raw text to a license in a group.
 */
export const addLicenseRawText = (
  licenseGroups: LicenseGroups,
  groupIndex: number,
  licenseIndex: number,
): LicenseGroups =>
  updateLicense(licenseGroups, groupIndex, licenseIndex, (license) => ({
    ...license,
    showRaw: true,
  }));

/**
 * Remove raw text from a license in a group.
 */
export const removeLicenseRawText = (
  licenseGroups: LicenseGroups,
  groupIndex: number,
  licenseIndex: number,
): LicenseGroups =>
  updateLicense(licenseGroups, groupIndex, licenseIndex, (license) => ({
    ...license,
    text: null,
    copyright: null,
    showRaw: false,
  }));

/**
 * Set the title of a license in a group.
 */
export const setLicenseTitle = (
  title: string,
  licenseGroups: LicenseGroups,
  groupIndex: number,
  licenseIndex: number,
): LicenseGroups =>
  updateLicense(licenseGroups, groupIndex, licenseIndex, (license) => ({
    ...license,
    title,
  }));

/**
 * Set the text of a license in a group.
 */
export const setLicenseText = (
  text: string,
  licenseGroups: LicenseGroups,
  groupIndex: number,
  licenseIndex: number,
): LicenseGroups =>
  updateLicense(licenseGroups, groupIndex, licenseIndex, (license) => ({
    ...license,
    text,
  }));

/**
 * Set the copyright of a license in a group.
 */
export const setLicenseCopyright = (
  copyright: string,
  licenseGroups: LicenseGroups,
  groupIndex: number,
  licenseIndex: number,
): LicenseGroups =>
  updateLicense(licenseGroups, groupIndex, licenseIndex, (license) => ({
    ...license,
    copyright,
  }));
